import { useRef, useEffect, useLayoutEffect, useState, forwardRef, ForwardedRef } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { reflow } from 'builder/utils/reflow'
import { useForwardedRef } from 'builder/hooks/useForwardedRef'
import { Icon20, Icon24 } from 'builder/components/Icon'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors } from 'builder/modules/resumeEditor'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { useScrollToElementOnMount } from 'builder/hooks/useScrollToElementOnMount'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import {
  MOBILE_SCROLL_TO_SUGGESTIONS_OFFSET,
  SCROLL_DELAY_MS,
  transitionDurationMs,
} from 'builder/components/PhrasesSuggestion'
import {
  Container,
  Header,
  Title,
  Description,
  Suggestion,
  Separator,
  Actions,
  AttemptsLeft,
  RetryButton,
  UseButton,
  Icon,
  CloseButton,
  Scroll,
  CloseButtonIconContainer,
} from 'builder/components/AiFeatures/AiProfileSummary/components/AIProfileSummaryResult/styles'
import { ANALYTICS_SECTION_NAME } from '../../constants'
import { Props } from './types'
import { useAiWorkExperienceResult } from './hooks/useAiWorkExperienceResult'
import { AI_WORK_EXPERIENCE_RESULT_MODAL } from './constants'

type Ref = ForwardedRef<HTMLDivElement | null>

export const AiWorkExperienceResult = forwardRef((props: Props, ref: Ref) => {
  const { i18n } = useI18n()
  const [isVisible, setVisible] = useState(false)
  const container = useForwardedRef(ref)
  const scroll = useRef<HTMLDivElement | null>(null)
  const suggestionRef = useRef<HTMLDivElement>(null)
  const { shouldAnimateResultPopupContent } = useTypedSelector(selectors.aiWorkExperience)
  const {
    generatedContent,
    attemptLeftText,
    isPremiumUser,
    isAnimationActive,
    handleRetry,
    handleUse,
    handleClose,
    animateContent,
    remainingAttempts,
  } = useAiWorkExperienceResult(container, suggestionRef, { ...props })
  const { isPhone, isTablet, isLaptop } = useMediaQueries()
  const isSmallDisplay = isPhone || isTablet || isLaptop

  // Start entering transition
  useLayoutEffect(() => {
    if (container.current) reflow(container.current)
    setVisible(true)
  }, [container])

  useEffect(() => {
    if (generatedContent && shouldAnimateResultPopupContent) {
      animateContent()
    }
  }, [animateContent, shouldAnimateResultPopupContent, generatedContent])

  useEffectOnMount(() => {
    trackInternalEvent('open_ai_suggestion_dialog', {
      section: ANALYTICS_SECTION_NAME,
      counter: remainingAttempts === Infinity ? 'unlimited' : remainingAttempts,
    })
  })

  useScrollToElementOnMount({
    ref: container,
    topOffset: MOBILE_SCROLL_TO_SUGGESTIONS_OFFSET,
    timeoutMs: SCROLL_DELAY_MS + transitionDurationMs,
    isDisabled: !isSmallDisplay,
  })

  return (
    <Container id={AI_WORK_EXPERIENCE_RESULT_MODAL} ref={container} isVisible={isVisible}>
      <CloseButton onClick={handleClose}>
        <CloseButtonIconContainer>
          <Icon24.Close />
        </CloseButtonIconContainer>
      </CloseButton>
      <Header>
        <Title>{i18n.t('builder.ai_profile_summary.rephrasing_modal.title')}</Title>
        <Description>
          {i18n.t('builder.ai_profile_summary.rephrasing_modal.description')}
        </Description>
      </Header>
      <Separator />
      <Scroll ref={scroll}>
        <Suggestion
          {...{ isAnimationActive }}
          ref={suggestionRef}
          dangerouslySetInnerHTML={{ __html: generatedContent || '' }}
        />
      </Scroll>
      <Actions>
        {!isPremiumUser && <AttemptsLeft>{attemptLeftText}</AttemptsLeft>}
        <RetryButton onClick={handleRetry}>
          <Icon>
            <Icon20.Update />
          </Icon>
          {i18n.t('builder.ai_profile_summary.rephrasing_modal.button_text.retry')}
        </RetryButton>
        <UseButton onClick={handleUse}>
          {i18n.t('builder.ai_profile_summary.rephrasing_modal.button_text.use')}
        </UseButton>
      </Actions>
    </Container>
  )
})
